import {computed, inject, Injectable, signal} from "@angular/core";
import {
	DeviceActivityType,
	ExamApplicationData,
	ExamApplicationDataType,
	FontAwesomeCode,
	Participant,
	SessionPhaseType,
	SessionRunParticipantStateType,
} from "@models";
import {EventEmitterService} from "@event-emitter";
import {BehaviorSubject} from "rxjs";
import {DeviceService, SimDeviceService, SimUtilityService, TypePredicates} from "@services";
import {CdkPortal} from "@angular/cdk/portal";
import {SimTimeService} from "./sim-time.service";
import {PhaseColorMapping} from "../../../shared/models/SimSessionPhasePalette";
import {AppDataSyncService} from "@common/services/app-data-sync.service";

export enum ParticipantDeviceType {
	Examiner,
	StandardizedPatient,
	StudentInside,
	StudentOutside,
}

export interface CurrentParticipant extends Participant {
	fontIcon: FontAwesomeCode;
	fullName: string;
}

export const PhaseTypeMapping = {
	[ExamApplicationDataType.StudentReady]: [SessionPhaseType.StudentReady],
	[ExamApplicationDataType.PreEncounter]: [SessionPhaseType.PreEncounter],
	[ExamApplicationDataType.Encounter]: [SessionPhaseType.Encounter],
	[ExamApplicationDataType.PostEncounter]: [SessionPhaseType.PostEncounter],
} as const;

@Injectable({
	providedIn: "root",
})
export class ParticipantDeviceService {
	// Signals
	private isLoading = signal<boolean>(true);
	public isAuth = signal<boolean>(false);
	public deviceType = computed<ParticipantDeviceType | null>(() => this._computeDeviceType());
	public currentParticipant = computed<CurrentParticipant | null>(() => this._computeCurrentParticipant(this.examState()));
	public phaseTheme = computed<string>(() => this._computePhaseTheme());
	public phaseStartTimeAdjusted = computed(() => this._timeService.getAdjustedLocalTime(this.examState()?.phaseStartTime));
	private _examState = signal<ExamApplicationData | null>(null);
	private _additionalButtonsPortal = signal<CdkPortal | null>(null);
	public examState = this._examState.asReadonly();
	public additionalButtonsPortal = this._additionalButtonsPortal.asReadonly();

	// Injections
	private _deviceService = inject(DeviceService);
	private _eventEmitter = inject(EventEmitterService);
	private _simDeviceService = inject(SimDeviceService);
	private _timeService = inject(SimTimeService);
	private _syncService = inject(AppDataSyncService);

	constructor() {
		this._eventEmitter.onApplicationDataChanged.subscribe(async (event) => {
			if (TypePredicates.isExamRunApplicationData(event.applicationData)) {
				this.isAuth.set(event.applicationData.participantStateType === SessionRunParticipantStateType.Ready);
			}

			if (TypePredicates.isExamApplicationData(event.applicationData)) {
				this.isLoading.set(true);
				this._examState.set(event.applicationData);
				this.isLoading.set(false);
			}
		});

		this._simDeviceService.readyToCommunicate(this._deviceService.device()?.autoLoginCapability.type ?? DeviceActivityType.None).then();
		this._eventEmitter.onSystemHubReconnected.subscribe(() => {
			this._simDeviceService
				.readyToCommunicate(this._deviceService.device()?.autoLoginCapability.type ?? DeviceActivityType.None)
				.then();
		});
		this._syncService.preventInfiniteLoading(this.isLoading, this._deviceService.device()?.autoLoginCapability.type ?? DeviceActivityType.None);
	}

	public setPortal(portal: CdkPortal): void {
		this._additionalButtonsPortal.set(portal);
	}

	private _computeDeviceType(): ParticipantDeviceType | null {
		switch (this._deviceService.device()?.autoLoginCapability.type) {
			case DeviceActivityType.ExamStandardizedPatientPc:
				return ParticipantDeviceType.StandardizedPatient;
			case DeviceActivityType.ExamExaminerPc:
				return ParticipantDeviceType.Examiner;
			case DeviceActivityType.ExamStudentInsidePc:
				return ParticipantDeviceType.StudentInside;
			case DeviceActivityType.ExamStudentOutsidePc:
				return ParticipantDeviceType.StudentOutside;
			default:
				return null;
		}
	}

	private _computeCurrentParticipant(examState: ExamApplicationData | null): CurrentParticipant | null {
		if (
			TypePredicates.isExamRunApplicationData(examState) &&
			examState.currentParticipant?.participantType in SimUtilityService.ParticipantType_FontAwesomeCode_Mapping
		) {
			return SimUtilityService.mapParticipantToParticipantDisplay(examState.currentParticipant);
		}
		return null;
	}

	private _computePhaseTheme(): string {
		const examState = this._examState();
		const phaseType =
			examState && examState.type in PhaseTypeMapping ? PhaseTypeMapping[examState.type as keyof typeof PhaseTypeMapping] : -1;
		return PhaseColorMapping[phaseType as SessionPhaseType];
	}
}
